'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { showNotification } from '@mantine/notifications';

// UI lib components
import { Stack, Container } from '@mantine/core';

// UI local components
import ConfirmBar from '@ui-kit/molecules/ConfirmBar';
import TextInput from '@ui-kit/molecules/TextInput';

// Store
import dialogStoreVar from '@features/Dialog/store/dialogStore';

// Services
import useUploader from '@shared/services/upload/useUploader';

// API & Types
import { ResourceScope } from '@app-types/blackroom-app';
import { DialogResourceCreateFolderProps } from './types';

// Style
import * as classes from './styles/index.css';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

/**
 * A self-managed dialog for creating new folders
 */
function DialogResourceCreateFolder({
  ...props
}: DialogResourceCreateFolderProps) {
  /* ********************************** HOOKS ********************************* */

  const [folderName, setFolderName] = useState('');

  // Store
  const dialogStore = useReactiveVar(dialogStoreVar);
  const { closeDialog } = dialogStore ?? {};

  // Uploader
  const { createFolder } = useUploader();

  // Routing
  const { folderId: parentId } = useParams<{
    scope: ResourceScope;
    folderId?: string;
  }>();

  // Locale
  const { t } = useTranslation();

  /* ****************************** SIDE EFFECTS ****************************** */

  useEffect(() => {
    // Reset form when dialog opens
    setFolderName('');
  }, []);

  /* ******************************** CALLBACKS ******************************* */

  const handleFolderNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFolderName(event.currentTarget.value);
    },
    [],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && isFormValid()) {
        event.preventDefault();
        onSubmit();
      }
    },
    [folderName],
  );

  async function onSubmit() {
    closeDialog?.('dialog-resource-create-folder');

    try {
      await createFolder?.(folderName.trim(), parentId);
    } catch (error) {
      showNotification({
        title: t('Common:errors.genericTitle'),
        message: t('Common:errors.genericMessage'),
        color: 'blackroomRed',
      });
      console.error(error);
    }
  }

  /* **************************** RENDERING HELPERS *************************** */

  const isFormValid = () => {
    return folderName.trim() !== '';
  };

  /* ******************************** RENDERING ******************************* */

  return (
    <Container className={classes.dialogResourceCreateFolder} fluid>
      <Stack align="stretch" justify="space-between" gap="lg" h="100%">
        <TextInput
          label={t('Resource:DialogResourceCreateFolder.fields.name.label')}
          value={folderName}
          onChange={handleFolderNameChange}
          onKeyDown={handleKeyDown}
          placeholder={t(
            'Resource:DialogResourceCreateFolder.fields.name.placeholder',
          )}
          autoFocus
        />

        <ConfirmBar
          labelProps={{
            confirm: t('Common:cta.create'),
            cancel: t('Common:cta.cancel'),
          }}
          onConfirm={onSubmit}
          onCancel={() => closeDialog?.('dialog-resource-create-folder')}
          disabled={!isFormValid()}
        />
      </Stack>
    </Container>
  );
}

/* **************************** DIALOG INVOKATION *************************** */

export default DialogResourceCreateFolder;
